import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Info, X } from 'lucide-react'
import { getGameType } from '../constants/gameTypes'

export default function GameTypeBadge({ gameTypeId, size = 'default' }) {
  const [showRules, setShowRules] = useState(false)
  const gameType = getGameType(gameTypeId)

  const label = gameType?.shortName || gameTypeId
  const isSmall = size === 'small'

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setShowRules(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            e.preventDefault()
            setShowRules(true)
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: isSmall ? '0.625rem' : '0.8125rem',
          fontWeight: isSmall ? '600' : '700',
          color: 'var(--accent-primary)',
          textTransform: 'uppercase',
          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
          padding: isSmall ? '0.125rem 0.375rem' : '0.1875rem 0.5rem',
          borderRadius: '9999px',
          letterSpacing: isSmall ? '0.05em' : '0.03em',
          cursor: 'pointer',
          lineHeight: 1.2,
        }}
      >
        {label}
        <Info size={isSmall ? 10 : 12} style={{ opacity: 0.7 }} />
      </span>

      {showRules && createPortal(
        <div
          onClick={() => setShowRules(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.75rem',
              maxWidth: '400px',
              width: '100%',
              overflow: 'hidden',
              animation: 'slideUp 0.3s ease',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--border-color)',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                {gameType?.name || gameTypeId}
              </h2>
              <button
                onClick={() => setShowRules(false)}
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  padding: '0.5rem',
                  borderRadius: '9999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  minHeight: '44px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.25rem' }}>
              {gameType ? (
                <>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    marginBottom: '1rem',
                  }}>
                    {gameType.description}
                  </p>

                  <h3 style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.5rem',
                  }}>
                    Rules
                  </h3>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.375rem',
                  }}>
                    {gameType.rules.map((rule, i) => (
                      <li key={i} style={{
                        color: 'var(--text-primary)',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                      }}>
                        {rule}
                      </li>
                    ))}
                  </ul>

                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Format</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                        {gameType.format === 'match_play' ? 'Match Play' : 'Stroke Play'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Scoring</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                        {gameType.scoring === 'nassau' ? 'Nassau (Front / Back / Overall)' : gameType.scoring === 'aggregate' ? 'Aggregate (18-hole total)' : 'Per 9'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  No rules available for this game type.
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
